import type { ApiErrorBody, ErrorCode } from '@l2u/shared/errors';
import { API_BASE } from './apiBase.ts';
import { getAdminAccessToken, setAdminAccessToken } from './auth/adminToken.ts';
import { getCompanyAccessToken, setCompanyAccessToken } from './auth/companyToken.ts';
import { getOpsAccessToken, setOpsAccessToken } from './auth/opsToken.ts';
import { getAdminSupabase, getOpsSupabase, getSupabase } from './supabase.ts';

export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode | 'network';
  readonly details: unknown;

  constructor(status: number, code: ErrorCode | 'network', message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

type Init = Omit<RequestInit, 'body' | 'signal'> & {
  body?: unknown;
  query?: Record<string, string | number | undefined | null>;
  /**
   * Aborts the request. React Query hands every queryFn one (`({ signal }) =>
   * companyApi.get(path, { signal })`) and aborts it when the query is
   * cancelled — a page left mid-load, a superseded key — so the browser
   * actually drops the request instead of finishing a download nobody reads.
   */
  signal?: AbortSignal;
};

/**
 * Whether `err` is a cancellation (an aborted fetch) rather than a failure.
 *
 * Request helpers rethrow an abort AS an abort — never wrapped as an ApiError
 * 'network' — so nothing mistakes a page the user navigated away from for a
 * dropped connection. Callers that report errors (toasts, inline messages)
 * should stay silent for these.
 */
export function isAbortError(err: unknown): boolean {
  return (
    typeof err === 'object' &&
    err !== null &&
    (err as { name?: unknown }).name === 'AbortError'
  );
}

async function request(
  path: string,
  init: Init = {},
  headers: Record<string, string> = {},
): Promise<unknown> {
  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  if (init.query) {
    for (const [k, v] of Object.entries(init.query)) {
      if (v === undefined || v === null) continue;
      url.searchParams.set(k, String(v));
    }
  }

  const finalHeaders: Record<string, string> = {
    Accept: 'application/json',
    ...headers,
    ...(init.headers as Record<string, string> | undefined),
  };

  const fetchInit: RequestInit = { headers: finalHeaders };
  if (init.method !== undefined) fetchInit.method = init.method;
  if (init.credentials !== undefined) fetchInit.credentials = init.credentials;
  if (init.signal !== undefined) fetchInit.signal = init.signal;
  if (init.body !== undefined && init.body !== null) {
    fetchInit.body = JSON.stringify(init.body);
    finalHeaders['Content-Type'] = 'application/json';
  }

  let res: Response;
  try {
    res = await fetch(url.toString(), fetchInit);
  } catch (err) {
    // A cancellation is not a network failure: rethrown untouched so it reads
    // as one (isAbortError), never as "check your connection".
    if (isAbortError(err)) throw err;
    throw new ApiError(0, 'network', err instanceof Error ? err.message : 'Network error');
  }

  if (res.status === 204) return null;

  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    const body = parsed as ApiErrorBody | null;
    const code = (body?.error?.code as ErrorCode | undefined) ?? 'internal';
    const message = body?.error?.message ?? `Request failed (${res.status})`;
    throw new ApiError(res.status, code, message, body?.error?.details);
  }

  return parsed;
}

function bearerHeader(token: string | null): Record<string, string> {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Single-flight refresh: if a 401 lands while another refresh is already
// running, all callers await the same promise rather than racing concurrent
// refreshSession() calls (which can rotate the refresh token).
let companyRefreshInFlight: Promise<string | null> | null = null;
let adminRefreshInFlight: Promise<string | null> | null = null;
let opsRefreshInFlight: Promise<string | null> | null = null;

async function refreshCompanySession(): Promise<string | null> {
  if (companyRefreshInFlight) return companyRefreshInFlight;
  companyRefreshInFlight = (async () => {
    try {
      const supabase = getSupabase();
      const { data, error } = await supabase.auth.refreshSession();
      if (error || !data.session) return null;
      setCompanyAccessToken(data.session.access_token);
      return data.session.access_token;
    } catch {
      return null;
    }
  })();
  try {
    return await companyRefreshInFlight;
  } finally {
    companyRefreshInFlight = null;
  }
}

async function refreshAdminSession(): Promise<string | null> {
  if (adminRefreshInFlight) return adminRefreshInFlight;
  adminRefreshInFlight = (async () => {
    try {
      const supabase = getAdminSupabase();
      const { data, error } = await supabase.auth.refreshSession();
      if (error || !data.session) return null;
      setAdminAccessToken(data.session.access_token);
      return data.session.access_token;
    } catch {
      return null;
    }
  })();
  try {
    return await adminRefreshInFlight;
  } finally {
    adminRefreshInFlight = null;
  }
}

async function refreshOpsSession(): Promise<string | null> {
  if (opsRefreshInFlight) return opsRefreshInFlight;
  opsRefreshInFlight = (async () => {
    try {
      const supabase = getOpsSupabase();
      const { data, error } = await supabase.auth.refreshSession();
      if (error || !data.session) return null;
      setOpsAccessToken(data.session.access_token);
      return data.session.access_token;
    } catch {
      return null;
    }
  })();
  try {
    return await opsRefreshInFlight;
  } finally {
    opsRefreshInFlight = null;
  }
}

async function requestWithRefresh(
  path: string,
  init: Init,
  getToken: () => string | null,
  refresh: () => Promise<string | null>,
): Promise<unknown> {
  try {
    return await request(path, init, bearerHeader(getToken()));
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401) throw err;
    const refreshed = await refresh();
    if (!refreshed) throw err;
    return await request(path, init, bearerHeader(refreshed));
  }
}

export const adminApi = {
  get<T>(path: string, init?: Init): Promise<T> {
    return requestWithRefresh(
      path,
      { ...init, method: 'GET' },
      getAdminAccessToken,
      refreshAdminSession,
    ) as Promise<T>;
  },
  post<T>(path: string, body?: unknown, init?: Init): Promise<T> {
    return requestWithRefresh(
      path,
      { ...init, method: 'POST', body },
      getAdminAccessToken,
      refreshAdminSession,
    ) as Promise<T>;
  },
  patch<T>(path: string, body?: unknown, init?: Init): Promise<T> {
    return requestWithRefresh(
      path,
      { ...init, method: 'PATCH', body },
      getAdminAccessToken,
      refreshAdminSession,
    ) as Promise<T>;
  },
  put<T>(path: string, body?: unknown, init?: Init): Promise<T> {
    return requestWithRefresh(
      path,
      { ...init, method: 'PUT', body },
      getAdminAccessToken,
      refreshAdminSession,
    ) as Promise<T>;
  },
  delete<T>(path: string, init?: Init): Promise<T> {
    return requestWithRefresh(
      path,
      { ...init, method: 'DELETE' },
      getAdminAccessToken,
      refreshAdminSession,
    ) as Promise<T>;
  },
};

/**
 * A raw authenticated GET for endpoints that return a FILE rather than JSON —
 * the compliance pack and its CSV. `adminApi.get` cannot serve these: it parses
 * the body as JSON and would swallow an HTML document.
 *
 * A plain `<a href>` cannot serve them either, because the browser sends no
 * bearer token on a navigation — which is why this exists at all. A 401 is
 * refreshed once the same way `requestWithRefresh` handles it, so an expired
 * session retries instead of downloading a login page.
 */
export async function adminFileRequest(path: string): Promise<Response> {
  const url = new URL(`${API_BASE}${path}`, window.location.origin).toString();
  let res: Response;
  try {
    res = await fetch(url, { headers: bearerHeader(getAdminAccessToken()) });
  } catch (err) {
    throw new ApiError(0, 'network', err instanceof Error ? err.message : 'Network error');
  }
  if (res.status === 401) {
    const refreshed = await refreshAdminSession();
    if (refreshed) res = await fetch(url, { headers: bearerHeader(refreshed) });
  }
  if (!res.ok) {
    const text = await res.text();
    let body: ApiErrorBody | null = null;
    try {
      body = JSON.parse(text) as ApiErrorBody;
    } catch {
      body = null;
    }
    throw new ApiError(
      res.status,
      body?.error?.code ?? 'internal',
      body?.error?.message ?? `Download failed (${res.status})`,
      body?.error?.details,
    );
  }
  return res;
}

// Ops console (super-admin) API. Mirrors adminApi with its own token holder and
// refresh path so an ops session is fully independent of the admin session.
export const opsApi = {
  get<T>(path: string, init?: Init): Promise<T> {
    return requestWithRefresh(
      path,
      { ...init, method: 'GET' },
      getOpsAccessToken,
      refreshOpsSession,
    ) as Promise<T>;
  },
  post<T>(path: string, body?: unknown, init?: Init): Promise<T> {
    return requestWithRefresh(
      path,
      { ...init, method: 'POST', body },
      getOpsAccessToken,
      refreshOpsSession,
    ) as Promise<T>;
  },
  patch<T>(path: string, body?: unknown, init?: Init): Promise<T> {
    return requestWithRefresh(
      path,
      { ...init, method: 'PATCH', body },
      getOpsAccessToken,
      refreshOpsSession,
    ) as Promise<T>;
  },
  put<T>(path: string, body?: unknown, init?: Init): Promise<T> {
    return requestWithRefresh(
      path,
      { ...init, method: 'PUT', body },
      getOpsAccessToken,
      refreshOpsSession,
    ) as Promise<T>;
  },
  delete<T>(path: string, init?: Init): Promise<T> {
    return requestWithRefresh(
      path,
      { ...init, method: 'DELETE' },
      getOpsAccessToken,
      refreshOpsSession,
    ) as Promise<T>;
  },
};

export const companyApi = {
  get<T>(path: string, init?: Init): Promise<T> {
    return requestWithRefresh(
      path,
      { ...init, method: 'GET' },
      getCompanyAccessToken,
      refreshCompanySession,
    ) as Promise<T>;
  },
  post<T>(path: string, body?: unknown, init?: Init): Promise<T> {
    return requestWithRefresh(
      path,
      { ...init, method: 'POST', body },
      getCompanyAccessToken,
      refreshCompanySession,
    ) as Promise<T>;
  },
  patch<T>(path: string, body?: unknown, init?: Init): Promise<T> {
    return requestWithRefresh(
      path,
      { ...init, method: 'PATCH', body },
      getCompanyAccessToken,
      refreshCompanySession,
    ) as Promise<T>;
  },
  put<T>(path: string, body?: unknown, init?: Init): Promise<T> {
    return requestWithRefresh(
      path,
      { ...init, method: 'PUT', body },
      getCompanyAccessToken,
      refreshCompanySession,
    ) as Promise<T>;
  },
  delete<T>(path: string, init?: Init): Promise<T> {
    return requestWithRefresh(
      path,
      { ...init, method: 'DELETE' },
      getCompanyAccessToken,
      refreshCompanySession,
    ) as Promise<T>;
  },
};

// Drivers authenticate through the same Supabase magic-link session as
// companies, so they share the bearer-token + refresh machinery. We expose
// a separate name purely for readability in driver code.
export const driverApi = {
  get<T>(path: string, init?: Init): Promise<T> {
    return requestWithRefresh(
      path,
      { ...init, method: 'GET' },
      getCompanyAccessToken,
      refreshCompanySession,
    ) as Promise<T>;
  },
  post<T>(path: string, body?: unknown, init?: Init): Promise<T> {
    return requestWithRefresh(
      path,
      { ...init, method: 'POST', body },
      getCompanyAccessToken,
      refreshCompanySession,
    ) as Promise<T>;
  },
  patch<T>(path: string, body?: unknown, init?: Init): Promise<T> {
    return requestWithRefresh(
      path,
      { ...init, method: 'PATCH', body },
      getCompanyAccessToken,
      refreshCompanySession,
    ) as Promise<T>;
  },
  put<T>(path: string, body?: unknown, init?: Init): Promise<T> {
    return requestWithRefresh(
      path,
      { ...init, method: 'PUT', body },
      getCompanyAccessToken,
      refreshCompanySession,
    ) as Promise<T>;
  },
  delete<T>(path: string, init?: Init): Promise<T> {
    return requestWithRefresh(
      path,
      { ...init, method: 'DELETE' },
      getCompanyAccessToken,
      refreshCompanySession,
    ) as Promise<T>;
  },
};

export const publicApi = {
  get<T>(path: string, init?: Init): Promise<T> {
    return request(path, { ...init, method: 'GET' }) as Promise<T>;
  },
  post<T>(path: string, body?: unknown, init?: Init): Promise<T> {
    return request(path, { ...init, method: 'POST', body }) as Promise<T>;
  },
};

/**
 * The shortest time one upload is given before it is abandoned. What the
 * timeout bounds is a connection that silently stalled, where the fetch would
 * otherwise hang forever and the upload queue with it — never a slow but
 * moving upload, so it scales with the file (uploadTimeoutMs).
 */
export const UPLOAD_TIMEOUT_MS = 5 * 60_000;

/**
 * The slowest uplink an upload is still allowed to finish on: 256 kbit/s.
 * Deliberately below one weak mobile link's ~0.5 Mbit/s, because the audit
 * queue runs three uploads at once on the same uplink, so each gets a third.
 */
export const UPLOAD_FLOOR_BITS_PER_SECOND = 256_000;

/**
 * The timeout for one file: five minutes, or as long as the file takes at the
 * floor rate, whichever is longer. A 25 MB bucket-cap file at 256 kbit/s is
 * 25e6 × 8 / 256e3 ≈ 782 s (~13 min); 100 MB of training content ≈ 3125 s
 * (~52 min). A 5 MB photo stays at the five-minute minimum.
 */
export function uploadTimeoutMs(sizeBytes: number): number {
  return Math.max(
    UPLOAD_TIMEOUT_MS,
    Math.ceil((sizeBytes * 8) / UPLOAD_FLOOR_BITS_PER_SECOND) * 1000,
  );
}

export async function uploadFileToSignedUrl(
  signedUrl: string,
  file: File,
  opts: { signal?: AbortSignal; timeoutMs?: number } = {},
): Promise<void> {
  // One controller for both reasons to stop: the timeout and the caller's own
  // signal. Built by hand rather than with AbortSignal.any/timeout — any() is
  // Safari 17.4+, newer than the phones the driver portal has to serve.
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, opts.timeoutMs ?? uploadTimeoutMs(file.size));
  const callerSignal = opts.signal;
  const forwardAbort = () => controller.abort();
  if (callerSignal?.aborted) controller.abort();
  else callerSignal?.addEventListener('abort', forwardAbort, { once: true });

  let res: Response;
  try {
    res = await fetch(signedUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
      body: file,
      signal: controller.signal,
    });
  } catch (err) {
    // A timeout is an ordinary upload failure — the caller's retry/error path,
    // not a silent cancellation. Only the CALLER's abort stays an abort.
    if (timedOut) {
      throw new ApiError(0, 'network', 'Upload timed out. Check your connection and try again.');
    }
    throw err;
  } finally {
    clearTimeout(timer);
    callerSignal?.removeEventListener('abort', forwardAbort);
  }
  if (!res.ok) {
    throw new ApiError(res.status, 'internal', `Upload failed (${res.status})`);
  }
}
